import { useEffect, useMemo } from 'react'
import { useStore } from 'react-redux'
import { useLDClient } from '@afterpaytouch/feature'
import type { CheckoutAppState } from '../../state'
import { api } from '../../env'
import { Features } from '../../model/features'
import { FEDERATION_OUTBOX_KEY, USER_JOURNEY_NAMES } from './constants'
import type { RecordCompletedJourneyPayload } from './contract'
import { datadogUserJourneySink, UserJourneySink } from './sink'

type FederationGateState = {
  checkout?: { initial?: { token?: CheckoutAppState['checkout']['initial']['token'] } }
  consumer?: { hasFeatureFlagsReady?: CheckoutAppState['consumer']['hasFeatureFlagsReady'] }
}

export const recordUserJourneyUrl = (baseUrl: string, token: string): string =>
  `${baseUrl.replace(/\/+$/, '')}/portal/consumers/checkout/v2/${encodeURIComponent(token)}/user-journey`

export const OUTBOX_MAX_ENTRIES = 10
export const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

type FederationOutboxEntry = { url: string; payload: RecordCompletedJourneyPayload; queuedAt: number }

const isOutboxEntry = (value: unknown): value is FederationOutboxEntry => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const { url, payload, queuedAt } = value as Partial<Record<keyof FederationOutboxEntry, unknown>>
  return (
    typeof url === 'string' &&
    typeof queuedAt === 'number' &&
    typeof payload === 'object' &&
    payload !== null &&
    typeof (payload as { journey_id?: unknown }).journey_id === 'string'
  )
}

const writeOutbox = (entries: FederationOutboxEntry[]): void => {
  try {
    window.localStorage.setItem(FEDERATION_OUTBOX_KEY, JSON.stringify(entries))
  } catch {
    // storage unavailable or full — the beacon still goes out, only durability is lost
  }
}

const readOutbox = (): FederationOutboxEntry[] => {
  try {
    const raw = window.localStorage.getItem(FEDERATION_OUTBOX_KEY)
    if (raw === null) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    const entries: unknown[] = Array.isArray(parsed) ? parsed : []
    const now = Date.now()
    const live = entries.filter((entry): entry is FederationOutboxEntry => isOutboxEntry(entry) && now - entry.queuedAt <= OUTBOX_MAX_AGE_MS)
    if (live.length !== entries.length) {
      writeOutbox(live)
    }
    return live
  } catch {
    return []
  }
}

const enqueue = (entry: FederationOutboxEntry): void => {
  const others = readOutbox().filter((queued) => queued.payload.journey_id !== entry.payload.journey_id)
  writeOutbox([...others, entry].slice(-OUTBOX_MAX_ENTRIES))
}

const dequeue = (journeyId: string): void => {
  writeOutbox(readOutbox().filter((queued) => queued.payload.journey_id !== journeyId))
}

const isRetryableStatus = (status: number): boolean => status >= 500 || status === 408 || status === 429

const inFlight = new Set<string>()

const postFederation = (url: string, payload: RecordCompletedJourneyPayload): void => {
  const request = fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  inFlight.add(payload.journey_id)
  void request
    .then((res) => {
      if (!isRetryableStatus(res.status)) {
        dequeue(payload.journey_id)
      }
    })
    .catch(() => {
      // best-effort beacon — a lost completion is acceptable, a surfaced error is not
    })
    .finally(() => {
      inFlight.delete(payload.journey_id)
    })
}

export const replayFederationOutbox = (): void => {
  try {
    for (const entry of readOutbox()) {
      if (inFlight.has(entry.payload.journey_id)) {
        continue
      }
      postFederation(entry.url, { ...entry.payload, is_retry: true })
    }
  } catch {
    // replay is best-effort
  }
}

export const createFederatedUserJourneySink = (base: UserJourneySink, getRecordUrl: () => string | undefined): UserJourneySink => ({
  journeyStarted: (event) => {
    base.journeyStarted(event)
  },
  journeyCompleted: (payload) => {
    base.journeyCompleted(payload)
    try {
      if (payload.journey_name !== USER_JOURNEY_NAMES.CHECKOUT) {
        return
      }
      const url = getRecordUrl()
      if (url === undefined) {
        return
      }
      enqueue({ url, payload, queuedAt: Date.now() })
      postFederation(url, payload)
    } catch {
      // federation must never break the base sink's reporting or the caller
    }
  },
})

export const useFederatedUserJourneySink = (base: UserJourneySink = datadogUserJourneySink): UserJourneySink => {
  const client = useLDClient()
  const store = useStore<FederationGateState>()

  useEffect(() => {
    replayFederationOutbox()
  }, [])

  return useMemo(
    () =>
      createFederatedUserJourneySink(base, () => {
        try {
          const state = store.getState()
          if (state.consumer?.hasFeatureFlagsReady !== true) {
            return undefined
          }
          if (client?.variation(Features.CASH_USER_JOURNEY_FEDERATION_ENABLED, false) !== true) {
            return undefined
          }
          const token = state.checkout?.initial?.token ?? ''
          return token === '' ? undefined : recordUserJourneyUrl(api.PORTAL_API_BASE_URL, token)
        } catch {
          return undefined
        }
      }),
    [base, client, store]
  )
}
